import { computed, ref, type ComputedRef, type Ref } from 'vue';

/**
 * The admin's light / dark theme (AB#21595).
 *
 * A module-level singleton so every component shares one source of truth: the
 * resolved theme, the `data-theme` attribute on <html>, the stored preference
 * and the subscribers all stay in step. The attribute is what the token
 * overrides in main.scss key off, so flipping it re-themes the whole app in one
 * paint.
 *
 * There are two things here, not one:
 *   - PREFERENCE is what the person chose: 'light', 'dark', or 'system'. It
 *     persists per device. The default is 'system' — a fresh visitor follows
 *     their OS / browser setting rather than being forced light.
 *   - RESOLVED is the theme actually applied: 'light' or 'dark'. When the
 *     preference is 'system' it tracks `prefers-color-scheme` and updates live
 *     when the OS flips.
 *
 * `onThemeChange` lets non-Vue code (the Zendesk widget) follow the resolved
 * theme without reaching into the app.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'admin_theme_v1';

const preference = ref<ThemePreference>('system');
const resolved = ref<ResolvedTheme>('light');

type Listener = (isDark: boolean) => void;
const listeners = new Set<Listener>();

const systemPrefersDark = (): boolean => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
};

const resolvePreference = (pref: ThemePreference): ResolvedTheme =>
  pref === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : pref;

const applyAttribute = (value: ResolvedTheme): void => {
  try {
    document.documentElement.setAttribute('data-theme', value);
  } catch {
    // No document (SSR/tests) — the refs still carry the value.
  }
};

const readStored = (): ThemePreference => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {
    // localStorage unavailable — fall through to the default.
  }
  return 'system';
};

const setResolved = (value: ResolvedTheme): void => {
  if (resolved.value === value) {
    applyAttribute(value);
    return;
  }
  resolved.value = value;
  applyAttribute(value);
  listeners.forEach((fn) => fn(value === 'dark'));
};

/**
 * Apply the stored preference before the app mounts, so the first paint is
 * already correct and there is no flash. Call once, from main.ts. Also wires the
 * OS listener so a 'system' preference tracks the browser live.
 */
export const initTheme = (): void => {
  preference.value = readStored();
  setResolved(resolvePreference(preference.value));

  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (preference.value === 'system') {
        setResolved(resolvePreference('system'));
      }
    });
  } catch {
    // No matchMedia — a 'system' preference just won't track live changes.
  }
};

/** The resolved theme right now, for non-reactive callers (e.g. the widget). */
export const currentIsDark = (): boolean => resolved.value === 'dark';

/**
 * Subscribe to resolved-theme changes. Returns an unsubscribe function. Does not
 * fire immediately — read `currentIsDark()` for the initial value, then subscribe.
 */
export const onThemeChange = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export interface UseTheme {
  theme: Ref<ResolvedTheme>;
  preference: Ref<ThemePreference>;
  isDark: ComputedRef<boolean>;
  setTheme: (value: ThemePreference) => void;
  toggle: () => void;
}

export const useTheme = (): UseTheme => {
  const isDark = computed(() => resolved.value === 'dark');

  const setTheme = (value: ThemePreference): void => {
    preference.value = value;
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Preference just won't persist across reloads; the session still themes.
    }
    setResolved(resolvePreference(value));
  };

  // The toggle sets an EXPLICIT light/dark from whatever is showing now. First
  // use turns a 'system' default into a deliberate choice; a person who wants to
  // go back to following the OS can be offered that later.
  const toggle = (): void => setTheme(resolved.value === 'dark' ? 'light' : 'dark');

  return { theme: resolved, preference, isDark, setTheme, toggle };
};
